import Component from '@glimmer/component'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import type { ReportData } from 'teacher-ui-client/components/reporting/reading-eggspress/read-aloud-fluency/students-table/report-loader'
import {
  serialize as serializeSort,
  deserialize as deserializeSort,
  updateSortColumns,
} from '@blakeelearning/data-tables/utils/sorting'
import type { SortingConfig } from '@blakeelearning/data-tables/utils/sorting'
import { getSortViewModel } from 'teacher-ui-client/utils/sorting'
import t from 'ember-intl/helpers/t'
import { on } from '@ember/modifier'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import formatValue from 'teacher-ui-client/helpers/format-value'
import joinList from 'teacher-ui-client/helpers/join-list'
import gradeName from 'teacher-ui-client/helpers/grade-name'
import { fn } from '@ember/helper'
import { service } from '@ember/service'
import type RouterService from '@ember/routing/router-service'

interface Signature {
  Element: HTMLTableElement
  Args: {
    isReloading?: boolean
    studentsReportData: ReportData['students']
    isSubco?: boolean
  }
}

export class StudentsTable extends Component<Signature> {
  @queryParam('reporting/ui-scope/rex/read-aloud-fluency/index') pageNumber!: number

  @queryParam('reporting/ui-scope/rex/read-aloud-fluency/index') perPage!: number

  @service router!: RouterService

  viewStudentDetails = (studentId: string) => {
    void this.router.transitionTo('reporting.ui-scope.rex.read-aloud-fluency.student', studentId)
  }

  <template>
    <table class="relative table-fixed" ...attributes>
      <thead>
        <tr class="bg-oceany-blue-100">
          <ThSort @sortKey="first_name" @text={{t "reporting.firstName"}} class="min-w-32" />
          <ThSort @sortKey="last_name" @text={{t "reporting.lastName"}} class="min-w-32" />
          <ThSort @sortKey="school_class_names" @text={{t "schoolClass.label"}} class="min-w-32" />
          {{#if @isSubco}}
            <ThSort @sortKey="teacher_names" @text={{t "reporting.teacher"}} class="min-w-32" />
            <ThSort @sortKey="grade_position" @text={{t "grade.label"}} class="w-24" />
          {{/if}}
          <ThSort @sortKey="total_assignments_completed" @text={{t "reporting.totalAssignmentsCompleted"}} />
          <ThSort @sortKey="average_lexile_level" @text={{t "reporting.averageLexileLevel"}} />
        </tr>
      </thead>
      <tbody class="{{if @isReloading 'opacity-50'}}">
        {{#each @studentsReportData as |student index|}}
          <tr
            class="even:bg-oceany-blue-50 hover:bg-oceany-blue-150 divide-x divide-white odd:bg-white"
            data-test-table-row="{{index}}"
          >
            <td
              role="button"
              class="text-oceany-blue-300 cursor-pointer truncate p-3 font-semibold"
              data-test-cell="first_name"
              {{on "click" (fn this.viewStudentDetails student.id)}}
            >
              {{student.first_name}}
            </td>
            <td
              role="button"
              class="text-oceany-blue-300 cursor-pointer truncate p-3 font-semibold"
              data-test-cell="last_name"
              {{on "click" (fn this.viewStudentDetails student.id)}}
            >
              {{student.last_name}}
            </td>
            <td class="truncate p-3" data-test-cell="school_class_names">
              {{joinList student.school_class_names ", "}}
            </td>
            {{#if @isSubco}}
              <td class="truncate p-3" data-test-cell="teacher_names">
                {{joinList student.teacher_names ", "}}
              </td>
              <td class="p-3" data-test-cell="grade_position">
                {{gradeName student.grade_position}}
              </td>
            {{/if}}
            <td class="p-3" data-test-cell="total_assignments_completed">
              {{formatValue student.total_assignments_completed}}
            </td>
            <td class="p-3" data-test-cell="average_lexile_formatted">
              {{formatValue student.average_lexile_formatted}}
            </td>
          </tr>
        {{/each}}
      </tbody>
    </table>
  </template>
}

interface ThSortSignature {
  Element: HTMLTableCellElement
  Args: {
    text?: string
    sortKey: string
  }
}

class ThSort extends Component<ThSortSignature> {
  @queryParam('reporting/ui-scope/rex/read-aloud-fluency/index') sort!: string

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  set sortingConfig(newSortConfig: SortingConfig) {
    this.sort = serializeSort(newSortConfig)
  }

  updateSort = () => {
    this.sortingConfig = updateSortColumns(this.sortingConfig, this.args.sortKey)
  }

  get sortViewModel() {
    return getSortViewModel(this.sortingConfig, this.args.sortKey)
  }

  <template>
    <th
      class="select-none"
      data-test-sort-key={{@sortKey}}
      aria-sort={{this.sortViewModel.ariaDirection}}
      ...attributes
    >
      <button
        type="button"
        class="flex h-full w-full items-center gap-x-3 p-3 text-left {{if @text 'justify-between' 'justify-end'}}"
        {{on "click" this.updateSort}}
      >
        {{#if @text}}<span class="text-dusty-black-300 font-semibold">{{@text}}</span>{{/if}}
        <PhosphorIcon @icon={{this.sortViewModel.icon}} class="text-dusty-black-400 print:hidden" />
      </button>
    </th>
  </template>
}
